import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  empty,
  isArray,
  isObject,
  prepareResponseData,
} from "../../Utilities/utils";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { AuthContext } from "../Root/ProtectedRoute";

// css
import "../students/Students.css";

// api
import classApi from "../../api/Classes";
import subjectApi from "../../api/Subject";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import { FaCheck, FaTimes } from "react-icons/fa";
import { Tag } from "primereact/tag";
import TableLoading from "../../components/skeleton/TableLoading";
import colors from "../../config/colors";

const SubjectsToAssess = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  //ref
  const toastTR = useRef(null);
  // states
  const [subjectData, setSubjectData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [classSubjects, setClassSubjects] = useState([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [subjectIds, setSubjectIds] = useState([]);
  const [classTitle, setClassTitle] = useState("");
  const [checked, setChecked] = useState(false);
  const [showAddButton, setShowAddButton] = useState(false);
  const paramState = !empty(location.state) ? location.state : {};
  const classId =
    !empty(paramState) && !empty(paramState.classId) ? paramState.classId : "";
  const schoolData =
    !empty(user.schoolData) && isObject(user.schoolData) ? user.schoolData : {};
  const sessionId =
    !empty(schoolData) && !empty(schoolData.currentSessionId)
      ? schoolData.currentSessionId
      : "";
  const termId =
    !empty(schoolData) && !empty(schoolData.currentTermId)
      ? schoolData.currentTermId
      : "";

  useEffect(() => {
    // fetch subject
    if (empty(classId) || empty(termId) || empty(sessionId)) {
      return navigate("/404");
    }
    getClassDetails();
  }, [user]);

  useEffect(() => {
    if (empty(selectedSubjectIds)) {
      setShowAddButton(false);
    } else {
      setShowAddButton(true);
    }
  }, [selectedSubjectIds, user]);

  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  const getSubjects = async (subjectsInClass) => {
    try {
      if (!isLoading) setIsLoading(true);
      const response = await classApi.getSubjectsInClass(
        classId,
        10000
      );
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        setSubjectData([]);
      } else {
        const results =
          !empty(response_data) &&
          !empty(response_data.response) &&
          isArray(response_data.response)
            ? response_data.response
            : [];
        let updatedSubjectids = [];
        let updatedResults = [];
        for (let i = 0; i < results.length; i++) {
          const subjectId =
            !empty(results[i]) && !empty(results[i]._id) ? results[i]._id : "";
          updatedSubjectids.push(subjectId);
          results[i].added = subjectsInClass.includes(subjectId);
          updatedResults.push(results[i]);
        }
        setSubjectIds(updatedSubjectids);
        setSubjectData(updatedResults);
      }
    } catch (error) {
      responseDialog("error", "Error Alert", "Failed to fetch subjects.");
    } finally {
      setIsLoading(false);
    }
  };

  const getClassDetails = async () => {
    try {
      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const response = await classApi.getSingleClass(classId, schoolId);
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.response) ||
        !response_data.response
      ) {
        responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Failed to fetch class details"
        );
      } else {
        const data =
          !empty(response_data) &&
          !empty(response_data.response) &&
          isObject(response_data.response)
            ? response_data.response
            : {};
        const classTitle = !empty(data.title) ? data.title : "";
        const reports = !empty(data.reports) ? data.reports : [];
        let currentReportSubjectIds = [];
        if (!empty(reports)) {
          for (let i = 0; i < reports.length; i++) {
            const itemTermId =
              !empty(reports[i]) && !empty(reports[i].termId)
                ? reports[i].termId
                : "";
            const itemSessionId =
              !empty(reports[i]) && !empty(reports[i].sessionId)
                ? reports[i].sessionId
                : "";
            if (itemTermId === termId && itemSessionId === sessionId) {
              const _reports = !empty(reports[i]) ? reports[i] : {};
              currentReportSubjectIds = !empty(_reports)
                ? _reports.subjectsToAssess
                : [];
              break;
            }
          }
        }
        const subjectsInClass = !empty(data.subjects) ? data.subjects : [];

        if (
          currentReportSubjectIds.length === subjectsInClass.length &&
          currentReportSubjectIds.length > 0
        ) {
          setChecked(true);
        } else {
          setChecked(false);
        }
        setSelectedSubjectIds(currentReportSubjectIds);
        setClassSubjects(subjectsInClass);
        setClassTitle(classTitle);

        getSubjects(subjectsInClass);
      }
    } catch (error) {
      responseDialog("error", "Error Alert", "Something went wrong.");
    }
  };

  const updateSelectedSubjectIds = (subjectId) => {
    try {
      let updatedSubjects = [];
      const index = selectedSubjectIds.indexOf(subjectId);
      if (index > -1) {
        updatedSubjects = selectedSubjectIds.filter((id) => id !== subjectId);
      } else {
        updatedSubjects = [...selectedSubjectIds, subjectId];
      }
      setSelectedSubjectIds(updatedSubjects);
      if (updatedSubjects.length !== subjectIds.length) {
        setChecked(false);
      } else {
        setChecked(true);
      }
    } catch (error) {}
  };

  const toggleSubjectsToAssess = async () => {
    try {
      if (!isActionLoading) setIsActionLoading(true);
      let _dataToUpdate = [];
      if (selectedSubjectIds.every((item) => classSubjects.includes(item))) {
        _dataToUpdate = selectedSubjectIds;
      } else {
        _dataToUpdate = [...new Set([...classSubjects, ...selectedSubjectIds])];
      }

      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const response = await classApi.postionClass(
        schoolId,
        classId,
        sessionId,
        termId,
        _dataToUpdate
      );
      const response_data = prepareResponseData(response);
      setSelectedSubjectIds([]);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        responseDialog(
          "error",
          "Something went wrong",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Operation failed"
        );
      } else {
        responseDialog("success", "Success", "Class positioned successfully");
        getClassDetails();
      }
    } catch (error) {
      responseDialog(
        "error",
        "Something went wrong",
        "Request failed please try again later"
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const positionSubject = async (subjectId) => {
    try {
      if (!isActionLoading) setIsActionLoading(true);
      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const staffId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await subjectApi.positionSubject(
        schoolId,
        classId,
        sessionId,
        termId,
        subjectId,
        staffId
      );
      const response_data = prepareResponseData(response);
      setSelectedSubjectIds([]);
      if (empty(response_data) || !response_data.success) {
        responseDialog(
          "error",
          "Something went wrong",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Operation failed"
        );
      } else {
        responseDialog("success", "Success", "Subject positioned successfully");
        getClassDetails();
      }
    } catch (error) {
      responseDialog(
        "error",
        "Something went wrong",
        "Request failed please try again later"
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const checkBoxBodyTemplate = (rowData) => {
    const subjectId = !empty(rowData) && !empty(rowData._id) ? rowData._id : "";
    return (
      <Checkbox
        color="#633ccd"
        checked={selectedSubjectIds.includes(subjectId)}
        onChange={() => updateSelectedSubjectIds(subjectId)}
      />
    );
  };

  const statusTemplateBody = (rowData) => {
    const subjectId = !empty(rowData) && !empty(rowData._id) ? rowData._id : "";
    const included = selectedSubjectIds.includes(subjectId);
    return (
      <Tag
        value={included ? <FaCheck /> : <FaTimes />}
        severity={included ? "success" : "danger"}
      ></Tag>
    );
  };

  const actionTemplate = (rowData) => {
    const subjectId = !empty(rowData) && !empty(rowData._id) ? rowData._id : "";
    return (
      <ButtonIcon
        borderColor={colors.primary}
        backgroundColor={colors.primary}
        color={colors.white}
        buttonText="Position Subject"
        marginRight={1}
        width={140}
        borderHover={false}
        height={30}
        onClick={() => positionSubject(subjectId)}
      />
    );
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="tableCard">
            <MainHeader
              children={
                showAddButton ? (
                  <>
                    <ButtonIcon
                      borderColor={colors.primary}
                      backgroundColor={colors.primary}
                      color={colors.white}
                      buttonText="Position Class"
                      marginRight={1}
                      width={170}
                      borderHover={false}
                      height={30}
                      onClick={() => toggleSubjectsToAssess()}
                    />
                  </>
                ) : (
                  ""
                )
              }
              redirect={false}
              title={`Add/Remove subject(s) to Assess in ${classTitle}`}
            />
            <div style={{ marginBottom: 10 }}>
              <span style={{ color: "red", fontStyle: "italic" }}>
                Add subjects that you intend the class positioning to be
                performed with.
              </span>
            </div>
            <div style={{ marginBottom: 10 }}>
              <span style={{ marginRight: 40 }}>
                <strong>No. of Subjects In class: </strong>
                {classSubjects.length}
              </span>
              <span>
                <strong>No. of Selected Subjects: </strong>
                {selectedSubjectIds.length}
              </span>
            </div>
            {!isLoading ? (
              <DataTable
                value={subjectData}
                stateKey="dt-state-demo-local"
                tableStyle={{ minWidth: "30rem" }}
              >
                <Column
                  field="_id"
                  header={
                    <Checkbox
                      checked={checked}
                      onChange={(e) => {
                        setChecked(e.checked);
                        if (e.checked) {
                          setShowAddButton(true);
                          setSelectedSubjectIds(subjectIds);
                        } else {
                          setSelectedSubjectIds([]);
                        }
                      }}
                    />
                  }
                  body={checkBoxBodyTemplate}
                />
                <Column field="title" header="Title" />
                <Column field="code" header="Code" />
                <Column
                  field="status"
                  header="Status"
                  body={statusTemplateBody}
                />
                <Column field="action" header="Action" body={actionTemplate} />
              </DataTable>
            ) : (
              <TableLoading cols={4} rows={20} header={false} />
            )}
          </div>
        </main>
        {isActionLoading && <FullPageLoader visible={isActionLoading} />}
      </AppWrapper>

      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default SubjectsToAssess;
